/**
 * ParticlePool - Object pool for particle effects
 *
 * Reuses Graphics objects for death effects, gate activations, etc.
 * Significantly reduces GC pressure during combat.
 *
 * Supported shapes:
 * - circle: Basic enemy death
 * - triangle: Enemy fragments
 * - hexagon: Elite enemy fragments
 * - square: Gate activation particles
 */

import Phaser from 'phaser';

export type ParticleShape = 'circle' | 'triangle' | 'hexagon' | 'square';

export interface ParticleConfig {
  x: number;
  y: number;
  shape: ParticleShape;
  color: number;
  alpha?: number;
  size: number;
  velocityX: number;
  velocityY: number;
  rotation?: number;
  duration: number;
  onComplete?: () => void;
}

interface PooledParticle {
  graphics: Phaser.GameObjects.Graphics;
  active: boolean;
  tween: Phaser.Tweens.Tween | null;
}

export class ParticlePool {
  private scene: Phaser.Scene;

  /** Pool of particle graphics objects */
  private pool: PooledParticle[] = [];

  /** Maximum pool size */
  private maxSize: number;

  /** Statistics */
  private stats = {
    created: 0,
    reused: 0,
    peakActive: 0,
  };

  constructor(scene: Phaser.Scene, initialSize: number = 100, maxSize: number = 200) {
    this.scene = scene;
    this.maxSize = maxSize;

    // Pre-allocate particles
    for (let i = 0; i < initialSize; i++) {
      this.pool.push(this.createParticle());
    }
  }

  /**
   * Creates a new pooled particle (hidden, inactive)
   */
  private createParticle(): PooledParticle {
    const graphics = this.scene.add.graphics();
    graphics.setVisible(false);
    this.stats.created++;

    return {
      graphics,
      active: false,
      tween: null,
    };
  }

  /**
   * Gets an inactive particle from the pool
   */
  private getInactiveParticle(): PooledParticle | null {
    // Find inactive particle
    let particle = this.pool.find(p => !p.active);

    if (!particle) {
      // Pool exhausted - try to create new
      if (this.pool.length < this.maxSize) {
        particle = this.createParticle();
        this.pool.push(particle);
      } else {
        // At max capacity
        return null;
      }
    } else {
      this.stats.reused++;
    }

    return particle;
  }

  /**
   * Spawns a particle with the given configuration
   */
  spawn(config: ParticleConfig): Phaser.GameObjects.Graphics | null {
    const particle = this.getInactiveParticle();
    if (!particle) return null;

    const { graphics } = particle;

    // Stop any existing tween
    if (particle.tween) {
      particle.tween.stop();
      particle.tween = null;
    }

    // Reset graphics
    graphics.clear();
    graphics.setPosition(config.x, config.y);
    graphics.setAlpha(config.alpha ?? 1);
    graphics.setScale(1);
    graphics.setRotation(0);
    graphics.setVisible(true);

    // Draw shape
    this.drawShape(graphics, config.shape, config.color, config.size, config.alpha ?? 0.8);

    // Mark as active
    particle.active = true;

    // Track peak usage
    const activeCount = this.getActiveCount();
    if (activeCount > this.stats.peakActive) {
      this.stats.peakActive = activeCount;
    }

    // Create animation tween
    particle.tween = this.scene.tweens.add({
      targets: graphics,
      x: config.x + config.velocityX * (config.duration / 1000),
      y: config.y + config.velocityY * (config.duration / 1000),
      alpha: 0,
      rotation: config.rotation ?? 0,
      duration: config.duration,
      ease: 'Power2',
      onComplete: () => {
        // Return to pool
        particle.active = false;
        graphics.setVisible(false);
        particle.tween = null;
        config.onComplete?.();
      },
    });

    return graphics;
  }

  /**
   * Spawns multiple particles in a burst pattern
   */
  spawnBurst(
    x: number,
    y: number,
    shape: ParticleShape,
    color: number,
    count: number,
    options?: {
      minSize?: number;
      maxSize?: number;
      minVelocity?: number;
      maxVelocity?: number;
      minDuration?: number;
      maxDuration?: number;
      alpha?: number;
    }
  ): void {
    const minSize = options?.minSize ?? 3;
    const maxSize = options?.maxSize ?? 6;
    const minVelocity = options?.minVelocity ?? 50;
    const maxVelocity = options?.maxVelocity ?? 100;
    const minDuration = options?.minDuration ?? 250;
    const maxDuration = options?.maxDuration ?? 400;
    const alpha = options?.alpha ?? 0.8;

    for (let i = 0; i < count; i++) {
      const angle = (i / count) * Math.PI * 2;
      const velocity = Phaser.Math.Between(minVelocity, maxVelocity);
      const vx = Math.cos(angle) * velocity;
      const vy = Math.sin(angle) * velocity;

      this.spawn({
        x,
        y,
        shape,
        color,
        alpha,
        size: Phaser.Math.Between(minSize, maxSize),
        velocityX: vx,
        velocityY: vy,
        rotation: Phaser.Math.FloatBetween(-Math.PI, Math.PI),
        duration: Phaser.Math.Between(minDuration, maxDuration),
      });
    }
  }

  /**
   * Draws a shape on the graphics object
   */
  private drawShape(
    graphics: Phaser.GameObjects.Graphics,
    shape: ParticleShape,
    color: number,
    size: number,
    alpha: number
  ): void {
    graphics.fillStyle(color, alpha);

    switch (shape) {
      case 'circle':
        graphics.fillCircle(0, 0, size / 2);
        break;

      case 'triangle': {
        const halfSize = size / 2;
        graphics.beginPath();
        graphics.moveTo(0, halfSize);
        graphics.lineTo(-halfSize, -halfSize);
        graphics.lineTo(halfSize, -halfSize);
        graphics.closePath();
        graphics.fillPath();
        break;
      }

      case 'hexagon': {
        const halfSize = size / 2;
        graphics.beginPath();
        for (let i = 0; i < 6; i++) {
          const angle = (i * Math.PI * 2) / 6 - Math.PI / 2;
          const px = Math.cos(angle) * halfSize;
          const py = Math.sin(angle) * halfSize;
          if (i === 0) {
            graphics.moveTo(px, py);
          } else {
            graphics.lineTo(px, py);
          }
        }
        graphics.closePath();
        graphics.fillPath();
        break;
      }

      case 'square':
        graphics.fillRect(-size / 2, -size / 2, size, size);
        break;
    }
  }

  /**
   * Gets count of currently active particles
   */
  getActiveCount(): number {
    return this.pool.filter(p => p.active).length;
  }

  /**
   * Gets total pool size
   */
  getTotalSize(): number {
    return this.pool.length;
  }

  /**
   * Gets pool utilization percentage
   */
  getUtilization(): number {
    if (this.pool.length === 0) return 0;
    return (this.getActiveCount() / this.pool.length) * 100;
  }

  /**
   * Gets pool statistics
   */
  getStats(): {
    created: number;
    reused: number;
    peakActive: number;
    currentActive: number;
    totalSize: number;
    utilization: number;
  } {
    return {
      ...this.stats,
      currentActive: this.getActiveCount(),
      totalSize: this.pool.length,
      utilization: this.getUtilization(),
    };
  }

  /**
   * Releases all active particles
   */
  releaseAll(): void {
    for (const particle of this.pool) {
      if (particle.active) {
        if (particle.tween) {
          particle.tween.stop();
          particle.tween = null;
        }
        particle.active = false;
        particle.graphics.setVisible(false);
      }
    }
  }

  /**
   * Cleanup - destroys all particles
   */
  destroy(): void {
    for (const particle of this.pool) {
      if (particle.tween) {
        particle.tween.stop();
      }
      particle.graphics.destroy();
    }
    this.pool = [];
  }
}

// Global particle pool instance (initialized by GameScene)
let globalParticlePool: ParticlePool | null = null;

/**
 * Gets the global particle pool
 */
export function getParticlePool(): ParticlePool | null {
  return globalParticlePool;
}

/**
 * Initializes the global particle pool
 */
export function initParticlePool(scene: Phaser.Scene): ParticlePool {
  if (globalParticlePool) {
    globalParticlePool.destroy();
  }
  globalParticlePool = new ParticlePool(scene, 100, 200);
  return globalParticlePool;
}

/**
 * Destroys the global particle pool
 */
export function destroyParticlePool(): void {
  if (globalParticlePool) {
    globalParticlePool.destroy();
    globalParticlePool = null;
  }
}
